/**
 * Helpers da Gestão de Solicitações (ASM/AST/ASP/RQA/RQI).
 * Formatação de datas/medidas e regras de seleção para lote MDF.
 */
import { TIPOS_SOLICITACAO } from "../../../services/solicitacoes";

export const TIPO_LABELS = Object.fromEntries(
  TIPOS_SOLICITACAO.map((t) => [t.value, t.label]),
);

export function unwrapLista(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
}

export function dataCurta(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  // Ano com 2 dígitos: cabe na coluna sem cortar ("07/07/26").
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString("pt-BR", {
        day: "2-digit", month: "2-digit", year: "2-digit",
      });
}

export function dataHora(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

export function tempoRelativo(iso) {
  if (!iso) return "—";
  const diffMs = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(diffMs) || diffMs < 0) return "agora";
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return "agora";
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h} h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `há ${d} d`;
  const m = Math.floor(d / 30);
  if (m < 12) return m === 1 ? "há 1 mês" : `há ${m} meses`;
  const a = Math.floor(m / 12);
  return a === 1 ? "há 1 ano" : `há ${a} anos`;
}

/** "C 778 (veio) × L 649 × E 18" — só inclui dimensões preenchidas. */
export function formatMedidas(peca) {
  if (!peca) return "";
  const partes = [];
  if (peca.comprimento_mm) {
    partes.push(`C ${peca.comprimento_mm}${peca.sentido_veio ? " (veio)" : ""}`);
  }
  if (peca.largura_mm) partes.push(`L ${peca.largura_mm}`);
  if (peca.espessura_mm) partes.push(`E ${peca.espessura_mm}`);
  return partes.join(" × ");
}

export function contagemItens(s) {
  if (s.familia === "requisicao") {
    const n = s.itens_count ?? 0;
    return `${n} ${n === 1 ? "item" : "itens"}`;
  }
  const n = s.pecas_count ?? 0;
  return `${n} ${n === 1 ? "peça" : "peças"}`;
}

export function chaveSolicitacao(s) {
  return `${s.familia}:${s.id}`;
}

export function elegivelParaLote(s) {
  return s.familia === "assistencia" && (s.pecas_elegiveis_count ?? 0) > 0;
}

export function motivoInelegivel(s) {
  if (s.familia === "requisicao") {
    return "Requisições não entram em lote MDF — apenas assistências com peças elegíveis.";
  }
  const novas = s.pecas_novas_count ?? 0;
  const emLote = s.pecas_em_lote_count ?? 0;
  if (novas === 0) {
    return "Só mão de obra — esta assistência não tem peça nova de MDF para corte.";
  }
  if (s.situacao === "ABERTA") {
    return "Autorize a solicitação primeiro — só autorizadas entram em lote.";
  }
  if (emLote >= novas) {
    return "As peças MDF desta solicitação já foram incluídas em um lote gerado.";
  }
  if (s.situacao === "RECUSADA" || s.situacao === "CANCELADA") {
    return "Solicitação recusada/cancelada não entra em lote.";
  }
  return "Esta assistência não tem peças MDF elegíveis para lote.";
}

/**
 * Vincula uma peça elegível às solicitações selecionadas. O shape do
 * endpoint pode referenciar a assistência por id ou por número; quando a
 * ligação não é identificável, mantém a peça (o gestor desmarca na tela).
 */
export function pecaDasSolicitacoes(peca, solicitacoes) {
  const ids = new Set(solicitacoes.map((s) => String(s.id)));
  const numeros = new Set(solicitacoes.map((s) => s.numero).filter(Boolean));

  let ref = peca.assistencia_id ?? peca.assistencia ?? peca.solicitacao_id ?? null;
  if (ref && typeof ref === "object") ref = ref.id ?? null;
  if (ref !== null && ref !== undefined) return ids.has(String(ref));

  const numero =
    peca.assistencia_numero ?? peca.solicitacao_numero ?? peca.numero_solicitacao;
  if (numero) return numeros.has(numero);

  return true;
}

/** Número da solicitação de origem da peça, quando o backend expõe. */
export function numeroSolicitacaoDaPeca(peca) {
  return (
    peca.assistencia_numero ??
    peca.solicitacao_numero ??
    peca.numero_solicitacao ??
    (typeof peca.assistencia === "object" ? peca.assistencia?.numero : null) ??
    ""
  );
}

// ---------------------------------------------------------------- junção
// Espelha `solicitacoes/juntar.py` só para orientar a seleção; quem decide é
// o backend (a prévia devolve o motivo exato quando algo não fecha).

const SITUACOES_ENCERRADAS = new Set(["ATENDIDA", "RECUSADA", "CANCELADA", "JUNTADA"]);

/** "" quando a solicitação pode entrar numa junção; senão o porquê. */
export function motivoNaoJuntavel(s) {
  if (SITUACOES_ENCERRADAS.has(s.situacao)) return "Solicitação encerrada não entra em junção.";
  if (!s.cliente) return "Cliente digitado à mão: só junta quem aponta para o cliente do cadastro.";
  if (s.familia === "assistencia" && (s.pecas_em_lote_count ?? 0) > 0) {
    return "Tem peça em lote de corte: não pode ser juntada.";
  }
  return "";
}

export function juntavel(s) {
  return motivoNaoJuntavel(s) === "";
}

/** "" quando as selecionadas formam uma junção válida; senão o porquê. */
export function motivoParaNaoJuntar(selecionadas) {
  if (selecionadas.length < 2) return "Selecione pelo menos duas solicitações para juntar.";
  const bloqueada = selecionadas.find((s) => !juntavel(s));
  if (bloqueada) return `${bloqueada.numero}: ${motivoNaoJuntavel(bloqueada)}`;
  const [primeira] = selecionadas;
  if (selecionadas.some((s) => s.tipo !== primeira.tipo)) return "Só junta solicitações do mesmo tipo.";
  if (selecionadas.some((s) => String(s.cliente) !== String(primeira.cliente))) {
    return "Só junta solicitações do mesmo cliente.";
  }
  if (selecionadas.some((s) => s.situacao !== primeira.situacao)) {
    return "Só junta solicitações na mesma situação.";
  }
  return "";
}

/**
 * A caixa de seleção serve a duas ações (lote MDF e junção): a linha é
 * selecionável se couber em ALGUMA que a pessoa pode fazer.
 */
export function regraDeSelecao({ podeGerarLote, podeJuntar }) {
  const pode = (s) => (podeGerarLote && elegivelParaLote(s)) || (podeJuntar && juntavel(s));
  const titulo = (s) => {
    if (pode(s)) {
      if (podeGerarLote && podeJuntar) return "Selecionar para lote MDF ou para juntar";
      return podeJuntar ? "Selecionar para juntar" : "Selecionar para lote MDF";
    }
    return podeJuntar ? motivoNaoJuntavel(s) : motivoInelegivel(s);
  };
  return { pode, titulo };
}

/** Sem permissão de juntar: a caixa serve só ao lote MDF (padrão das vistas). */
export const SELECAO_SO_LOTE = regraDeSelecao({ podeGerarLote: true, podeJuntar: false });
